import axios from 'axios';
import { NotificationManager } from 'react-notifications';
import { store } from '../redux/store';

const defaultOptions = {
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
};

// Create instance
let instance = axios.create(defaultOptions);

// Add library utilities to instance for convenience
instance.isCancel = axios.isCancel;
instance.CancelToken = axios.CancelToken;
instance.isAxiosError = axios.isAxiosError;
instance.all = axios.all;
instance.spread = axios.spread;

// Set the AUTH token for any request
instance.interceptors.request.use(function (config) {
  const token = localStorage.getItem('token');
  config.headers.Authorization = token ? `Bearer ${token}` : '';
  return config;
});

// Handle response errors
instance.interceptors.response.use(
  response => response,
  error => {
    // Ignore canceled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const disableNotification = error.config?.disableNotification; // Check for disableNotification parameter

    if (error.response?.status === 429) {
      // Too Many Requests error
      if (!disableNotification) {
        NotificationManager.error(
          'Too many requests. Please wait 1 minute before trying again.',
          'Rate Limit Exceeded',
          6000
        );
      }
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(instance(error.config));
        }, 60000); // Wait 1 minute before retrying
      });
    }

    if (error.response?.data?.error) {
      const errorData = error.response.data.error;
      console.error(errorData);
      if (!disableNotification) {
        // Convert error to string if it's an object
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          // If it's a validation error object, format it properly
          const errorMessages = [];
          for (const [key, value] of Object.entries(errorData)) {
            if (Array.isArray(value)) {
              value.forEach(msg => errorMessages.push(`${key}: ${msg}`));
            } else if (typeof value === 'string') {
              errorMessages.push(`${key}: ${value}`);
            } else {
              errorMessages.push(`${key}: ${JSON.stringify(value)}`);
            }
          }
          errorMessages.forEach(msg => NotificationManager.error(msg));
        } else if (typeof errorData === 'string') {
          NotificationManager.error(errorData);
        } else {
          NotificationManager.error(String(errorData));
        }
      }
    } else if (error.response?.data?.message) {
      console.error(error.response.data.message);
      if (!disableNotification) {
        NotificationManager.error(String(error.response.data.message));
      }
    } else {
      console.error(error.message);
      if (!disableNotification) {
        NotificationManager.error(String(error.message));
      }
    }

    // Ako je 401 (Unauthorized), automatski odjavi korisnika
    // (osim ako nije logout ruta ili login ruta)
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Ne odjavljuj ako je već logout ili login ruta
      if (!url.includes('/logout') && !url.includes('/login')) {
        // Odjavi korisnika
        store.dispatch({ type: 'LOGOUT' });
        // Signaliziraj drugim tabovima
        localStorage.setItem('logout', Date.now().toString());
      }
    }

    return Promise.reject(error);
  }
);

export default instance;